/**
 * Store Setup - Legend-State Sync Plugin Configuration
 *
 * Web version of mobile's syncPluginStore
 * Uses IndexedDB for persistence instead of MMKV
 *
 * ARCHITECTURE NOTE:
 * - Uses configureSynced + syncObservable + observablePersistIndexedDB (Legend-State v3)
 * - Business modules provide table names via initializeIndexedDBStores()
 * - Core module does NOT know about business store names
 * - Lazy initialization - only runs on browser when first store is created
 *
 * FLOW:
 * 1. App startup calls initializeIndexedDBStores() with ALL table names
 * 2. configureSynced sets up the IndexedDB plugin configuration
 * 3. syncObservable is used for each store (handles async properly)
 */

import { observable, type Observable, type Change } from '@legendapp/state';
import { configureSynced, syncObservable } from '@legendapp/state/sync';
import { observablePersistIndexedDB } from '@legendapp/state/persist-plugins/indexeddb';

const DB_NAME = 'kidsplaza-website-v2';
const DB_VERSION = 5; // Incremented for giftSelection table

/**
 * Track if Legend-State persistence has been initialized
 */
let isPersistenceInitialized = false;

/**
 * Track if initialization is in progress
 */
let initializationPromise: Promise<void> | null = null;

/**
 * Configured table names (set during initialization)
 */
let configuredTableNames: string[] = [];

/**
 * The configured persist options function (lazy loaded)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let persistOptions: ReturnType<typeof configureSynced> | null = null;

/**
 * Queue of stores waiting for persistence to be initialized
 */
/**
 * Sanitize Legend-State primitive wrappers from IndexedDB hydration.
 *
 * Legend-State v3 beta can corrupt primitive values when persisting to IndexedDB,
 * storing them as objects like { __legend_primitive: "actualValue", id: "fieldName" }
 * instead of plain strings/numbers. This function recursively unwraps them.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sanitizeLegendPrimitives = (value: any): any => {
    if (value === null || value === undefined) return value;

    // Unwrap __legend_primitive wrapper
    if (typeof value === 'object' && '__legend_primitive' in value) {
        return value.__legend_primitive;
    }

    // Recurse into plain objects (not arrays, dates, etc.)
    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        const result: Record<string, unknown> = {};
        for (const key of Object.keys(value)) {
            result[key] = sanitizeLegendPrimitives(value[key]);
        }
        return result;
    }

    // Recurse into arrays
    if (Array.isArray(value)) {
        return value.map(sanitizeLegendPrimitives);
    }

    return value;
};

/**
 * Create a sync transform that sanitizes __legend_primitive wrappers on load.
 */
const createSanitizeTransform = () => ({
    load: (value: unknown) => sanitizeLegendPrimitives(value),
    save: (value: unknown) => sanitizeLegendPrimitives(value),
});

const pendingStores: Array<{ store$: Observable<unknown>; storeName: string; itemID?: string }> = [];

/**
 * Hydration state observables
 */
export const hydrationState$ = observable({
    isHydrated: false,
    isLoading: true,
    hydratedStores: new Set<string>(),
});

/**
 * Mark a store as hydrated
 */
const markStoreAsHydrated = (storeName: string): void => {
    // Use requestAnimationFrame for smoother UI updates
    if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
            const currentStores = hydrationState$.hydratedStores.peek();
            currentStores.add(storeName);
            hydrationState$.hydratedStores.set(new Set(currentStores));
            hydrationState$.isHydrated.set(true);
            hydrationState$.isLoading.set(false);
        });
    } else {
        setTimeout(() => {
            const currentStores = hydrationState$.hydratedStores.peek();
            currentStores.add(storeName);
            hydrationState$.hydratedStores.set(new Set(currentStores));
            hydrationState$.isHydrated.set(true);
            hydrationState$.isLoading.set(false);
        }, 0);
    }
};

/**
 * Initialize IndexedDB persistence with the given table names
 *
 * This MUST be called at app startup with ALL table names that will be used.
 * Safe to call multiple times - will only initialize once.
 *
 * @param tableNames - All table names to create in IndexedDB
 *
 * @example
 * ```typescript
 * // In _app.tsx or app entry point
 * import { initializeIndexedDBStores } from '@kidslabs/module-core';
 *
 * // Initialize with all business store names
 * await initializeIndexedDBStores([
 *   'cart',
 *   'checkout',
 *   'customer',
 *   'auth',
 *   // ... all other store names
 * ]);
 * ```
 */
export const initializeIndexedDBStores = async (tableNames: string[]): Promise<void> => {
    // Only run on browser
    if (typeof window === 'undefined') {
        return;
    }

    // Return existing promise if initialization is already in progress
    if (initializationPromise) {
        return initializationPromise;
    }

    // Already initialized
    if (isPersistenceInitialized) {
        return;
    }

    // Validate input
    if (!tableNames || tableNames.length === 0) {
        console.warn('[IndexedDB] No table names provided. Call initializeIndexedDBStores with table names.');
        return;
    }

    configuredTableNames = [...tableNames];

    initializationPromise = (async () => {
        try {
            // Configure the synced options with IndexedDB plugin (Legend-State v3 pattern)
            persistOptions = configureSynced({
                persist: {
                    plugin: observablePersistIndexedDB({
                        databaseName: DB_NAME,
                        version: DB_VERSION,
                        tableNames: configuredTableNames,
                    }),
                },
            });

            isPersistenceInitialized = true;

            // Process any pending stores that were created before initialization
            await processPendingStores();
        } catch (error) {
            console.error('[IndexedDB] Failed to initialize persistence:', error);
            // Reset so it can be retried
            initializationPromise = null;
        }
    })();

    return initializationPromise;
};

/**
 * Process stores that were created before persistence was initialized
 */
const processPendingStores = async (): Promise<void> => {
    if (!persistOptions) {
        return;
    }

    while (pendingStores.length > 0) {
        const { store$, storeName, itemID } = pendingStores.shift()!;
        try {
            const options = itemID
                ? { persist: { name: storeName, indexedDB: { itemID } }, transform: createSanitizeTransform() }
                : { persist: { name: storeName }, transform: createSanitizeTransform() };

            syncObservable(store$, persistOptions(options));
            markStoreAsHydrated(storeName);
        } catch (error) {
            console.warn(`[createPersistedStore] Failed to persist pending store "${storeName}":`, error);
        }
    }
};

/**
 * Apply persistence to a store using syncObservable
 */
const applyPersistence = (store$: Observable<unknown>, storeName: string, itemID?: string): void => {
    if (!persistOptions) {
        console.warn(`[IndexedDB] Cannot persist store "${storeName}" - persistence not initialized`);
        return;
    }

    const options = itemID
        ? { persist: { name: storeName, indexedDB: { itemID } }, transform: createSanitizeTransform() }
        : { persist: { name: storeName }, transform: createSanitizeTransform() };

    syncObservable(store$, persistOptions(options));
    markStoreAsHydrated(storeName);
};

/**
 * Get all configured table names
 * Useful for debugging
 */
export const getRegisteredStores = (): string[] => {
    return [...configuredTableNames];
};

/**
 * Check if persistence is initialized and ready
 */
export const isIndexedDBReady = (): boolean => {
    return isPersistenceInitialized;
};

/**
 * Wait for IndexedDB to be ready
 * Note: initializeIndexedDBStores must be called first with table names
 */
export const waitForIndexedDB = async (): Promise<void> => {
    if (isPersistenceInitialized) {
        return;
    }
    if (initializationPromise) {
        await initializationPromise;
    }
};

/**
 * Clear specific tables from IndexedDB directly.
 * Use this before page reload to ensure stale persisted data
 * doesn't hydrate back on the next page load.
 *
 * @param tableNames - Names of tables to clear (must match store names)
 * @returns Promise that resolves when all tables are cleared
 */
export const clearPersistedStores = async (tableNames: string[]): Promise<void> => {
    if (typeof window === 'undefined') return;

    try {
        const db = await new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });

        const existingStores = Array.from(db.objectStoreNames);
        const storesToClear = tableNames.filter(name => existingStores.includes(name));

        if (storesToClear.length === 0) {
            db.close();
            return;
        }

        const tx = db.transaction(storesToClear, 'readwrite');
        await Promise.all(
            storesToClear.map(
                name =>
                    new Promise<void>((resolve, reject) => {
                        const req = tx.objectStore(name).clear();
                        req.onsuccess = () => resolve();
                        req.onerror = () => reject(req.error);
                    })
            )
        );

        db.close();
        console.log(`[IndexedDB] Cleared tables: ${storesToClear.join(', ')}`);
    } catch (error) {
        console.error('[IndexedDB] Failed to clear persisted stores:', error);
    }
};

/**
 * Parameters for sync get callback (matching mobile's SyncedGetParams)
 */
export interface SyncedGetParams<T> {
    value: T;
}

/**
 * Parameters for sync set callback (matching mobile's SyncedSetParams)
 *
 * NOTE: `changes` is Legend-State's Change[] array, not Partial<T>
 * Each Change contains: { path, pathTypes, valueAtPath, prevAtPath }
 */
export interface SyncedSetParams<T> {
    value: T;
    changes: Change[];
}

/**
 * Options for syncPluginWebStore (matches mobile's syncPluginStore config)
 */
export interface SyncPluginWebStoreOptions<T> {
    persist?: {
        name: string;
        mmkv?: { id: string };
    };
    initial?: T;
    get?: (params: SyncedGetParams<T>) => Promise<T> | T;
    set?: (params: SyncedSetParams<T>) => Promise<T | void> | T | void;
    debounceSet?: number;
}

/**
 * syncPluginStore - Web version matching mobile's API
 *
 * Returns configuration for observable initialization
 * This matches mobile's configureSynced pattern
 *
 * NOTE: On web, get/set callbacks are not automatically invoked.
 * Use syncPluginWebStore for full reactive sync support.
 *
 * @param options - Store configuration
 * @returns Initial state (similar to mobile's synced observable)
 */
export const syncPluginStore = <T extends object>(options: SyncPluginWebStoreOptions<T>): T => {
    // On web, we just return initial state
    // Persistence is handled by syncPluginWebStore wrapper
    return (options.initial ?? {}) as T;
};

/**
 * Create a synchronized observable store with IndexedDB persistence
 *
 * @param options - Store configuration options
 * @returns Observable store with persistence
 *
 * @example
 * ```typescript
 * const cartStore$ = syncPluginWebStore({
 *   persist: { name: 'cart' },
 *   initial: { items: [], total: 0 }
 * });
 * ```
 */
export const syncPluginWebStore = <T extends object>(options: SyncPluginWebStoreOptions<T>): Observable<T> => {
    const initial = options.initial ?? ({} as T);
    const store$ = observable<T>(initial);
    const storeName = options.persist?.name;

    // Set up persistence with IndexedDB if persist option provided
    // Only on client-side to prevent SSR errors
    if (storeName && typeof window !== 'undefined') {
        hydrationState$.isLoading.set(true);

        // Check if persistence is ready
        if (isPersistenceInitialized && persistOptions) {
            // Persistence is ready, set up immediately
            applyPersistence(store$ as Observable<unknown>, storeName);
        } else {
            // Persistence not ready yet, queue the store
            pendingStores.push({ store$: store$ as Observable<unknown>, storeName });
        }
    } else {
        // On server-side, mark as hydrated immediately
        if (storeName) {
            const currentStores = hydrationState$.hydratedStores.peek();
            currentStores.add(storeName);
            hydrationState$.hydratedStores.set(new Set(currentStores));
        }
        hydrationState$.isHydrated.set(true);
        hydrationState$.isLoading.set(false);
    }

    return store$;
};

/**
 * Check if a specific store has been hydrated
 */
export const isStoreHydrated = (storeName: string): boolean => {
    return hydrationState$.hydratedStores.peek().has(storeName);
};

/**
 * Wait for all stores to be hydrated
 */
export const waitForHydration = (): Promise<void> => {
    return new Promise(resolve => {
        if (hydrationState$.isHydrated.peek()) {
            resolve();
            return;
        }

        const unsubscribe = hydrationState$.isHydrated.onChange(isHydrated => {
            if (isHydrated) {
                unsubscribe();
                resolve();
            }
        });
    });
};

/**
 * Create a persisted observable store (simplified API)
 *
 * Automatically handles:
 * - SSR/client-side logic - no manual checks needed!
 * - Persistence configuration - uses Legend-State's syncObservable
 *
 * IMPORTANT: initializeIndexedDBStores() must be called at app startup
 * with all table names before any stores are created!
 *
 * @param storeName - Name of the store (table name in IndexedDB)
 * @param initialState - Initial state of the store
 * @param itemID - Optional itemID for Mode 2 persistence (single object in table)
 * @returns Observable store with automatic persistence
 *
 * @example
 * ```typescript
 * // In module-checkout
 * export const checkoutStore$ = createPersistedStore('checkout', {
 *   cartId: '',
 *   step: CheckoutStep.SHIPPING_ADDRESS,
 *   isLoading: false,
 * });
 *
 * // With itemID for single object persistence
 * export const settingsStore$ = createPersistedStore('settings', { theme: 'light' }, 'app-settings');
 * ```
 */
export const createPersistedStore = <T extends object>(
    storeName: string,
    initialState: T,
    itemID?: string
): Observable<T> => {
    const store$ = observable<T>(initialState);

    // Only set up persistence on client-side
    // On server-side, store works as regular observable without persistence
    if (typeof window !== 'undefined') {
        hydrationState$.isLoading.set(true);

        // Check if persistence is ready
        if (isPersistenceInitialized && persistOptions) {
            // Persistence is ready, set up persistence immediately
            applyPersistence(store$ as Observable<unknown>, storeName, itemID);
        } else {
            // Persistence not ready yet, queue the store
            pendingStores.push({ store$: store$ as Observable<unknown>, storeName, itemID });
        }
    } else {
        // Server-side: mark as hydrated immediately
        const currentStores = hydrationState$.hydratedStores.peek();
        currentStores.add(storeName);
        hydrationState$.hydratedStores.set(new Set(currentStores));
        hydrationState$.isHydrated.set(true);
        hydrationState$.isLoading.set(false);
    }

    return store$;
};

/**
 * Create a non-persisted observable store (for transient data)
 *
 * Use this for stores that should NOT persist (e.g., loading states, temporary UI state)
 *
 * @param initialState - Initial state of the store
 * @returns Observable store without persistence
 *
 * @example
 * ```typescript
 * // For transient data that should not persist
 * export const uiStore$ = createStore({
 *   isModalOpen: false,
 *   currentTab: 'home',
 * });
 * ```
 */
export const createStore = <T extends object>(initialState: T): Observable<T> => {
    return observable<T>(initialState);
};

/**
 * ============================================================================
 * SYNCED PERSISTED STORE - Full sync callback support (matching mobile)
 * ============================================================================
 *
 * This function creates a persisted store with automatic sync callbacks
 * that match mobile's syncPluginStore behavior.
 *
 * KEY DIFFERENCES FROM createPersistedStore:
 * - Supports `set` callback for sync operations
 * - Supports `debounceSet` for debounced sync
 * - Matches mobile's syncPluginStore API
 *
 * @param options - Store configuration matching mobile's syncPluginStore
 * @returns Observable store with persistence and sync callbacks
 *
 * @example
 * ```typescript
 * export const cartItemReferenceStore$ = createSyncedPersistedStore<CartItemReferenceStore>({
 *   persist: {
 *     name: 'cartItemReference',
 *   },
 *   initial: {
 *     id: 'cart-item-reference-store',
 *     items: [],
 *   },
 *   set: async (params) => {
 *     // Sync logic here - called automatically when store changes
 *     console.log('Store changed:', params.value);
 *     return params.value;
 *   },
 *   debounceSet: 1500, // Optional: debounce set callback
 * });
 * ```
 */
export const createSyncedPersistedStore = <T extends object>(options: SyncPluginWebStoreOptions<T>): Observable<T> => {
    const initial = options.initial ?? ({} as T);
    const store$ = observable<T>(initial);
    const storeName = options.persist?.name;

    // Only set up persistence and sync on client-side
    if (typeof window !== 'undefined' && storeName) {
        hydrationState$.isLoading.set(true);

        // ════════════════════════════════════════════════════════════════════
        // PHASE 1: Set up IndexedDB persistence
        // ════════════════════════════════════════════════════════════════════
        if (isPersistenceInitialized && persistOptions) {
            applyPersistence(store$ as Observable<unknown>, storeName);
        } else {
            pendingStores.push({ store$: store$ as Observable<unknown>, storeName });
        }

        // ════════════════════════════════════════════════════════════════════
        // PHASE 2: Set up sync callback (matching mobile's set callback)
        // ════════════════════════════════════════════════════════════════════
        if (options.set) {
            let debounceTimer: ReturnType<typeof setTimeout> | null = null;

            // Subscribe to store changes
            store$.onChange(({ value, changes }) => {
                const setCallback = options.set!;
                const debounceMs = options.debounceSet ?? 0;

                const executeSet = async () => {
                    try {
                        await setCallback({
                            value: value as T,
                            changes,
                        });
                    } catch (error) {
                        console.error(`[SyncedPersistedStore] Set callback failed for "${storeName}":`, error);
                    }
                };

                // Apply debounce if configured
                if (debounceMs > 0) {
                    if (debounceTimer) {
                        clearTimeout(debounceTimer);
                    }
                    debounceTimer = setTimeout(executeSet, debounceMs);
                } else {
                    // Execute immediately
                    void executeSet();
                }
            });
        }
    } else {
        // Server-side: mark as hydrated immediately
        if (storeName) {
            const currentStores = hydrationState$.hydratedStores.peek();
            currentStores.add(storeName);
            hydrationState$.hydratedStores.set(new Set(currentStores));
        }
        hydrationState$.isHydrated.set(true);
        hydrationState$.isLoading.set(false);
    }

    return store$;
};
